// AyurvedaPage.jsx
import React from 'react';
import { useTranslation } from 'react-i18next'; // Important
import { motion } from 'framer-motion';
import ayurvedaImage from '../assets/Ayurveda/Blog-Cover-3.jpg';
import ayurvedaFaceImage from '../assets/Ayurveda/a-guide-to-sri-lanka-wellness-holidays-part-3-ayurveda-header.jpg';
import ayurvedaMassageImage from '../assets/Ayurveda/Spa-&-Ayurveda-header.jpg';

const AyurvedaPage = () => {
    const { t } = useTranslation('ayurveda'); // Using 'ayurveda' namespace

    return (
        <div className="bg-[#001F3F] py-10">
            <div className="container mx-auto px-4">
                {/* Page Title */}
                <h1 className="text-center text-4xl md:text-5xl lg:text-6xl font-bold font-konkhmer mb-16 text-[#7FDBFF]">
                    {t('title')}
                </h1>

                {/* Main Content: Text + Image */}
                <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
                    {/* Left: Text Content */}
                    <div className="w-full lg:w-2/3 space-y-8 text-white font-inknut">
                        <p className="text-base md:text-lg lg:text-xl leading-relaxed">
                            {t('introParagraph1')}
                        </p>

                        <div>
                            <h3 className="text-[#0074D9] text-lg md:text-xl font-semibold mb-3">
                                {t('corePrinciplesTitle')}
                            </h3>
                            <ul className="list-disc list-inside space-y-2 text-base md:text-lg lg:text-xl">
                                <li>{t('principle1')}</li>
                                <li>{t('principle2')}</li>
                                <li>{t('principle3')}</li>
                            </ul>
                        </div>

                        <p className="text-base md:text-lg lg:text-xl leading-relaxed">
                            {t('sriLankaDoctors')}
                        </p>

                        <ul className="list-disc list-inside space-y-2 text-base md:text-lg lg:text-xl">
                            <li>{t('benefit1')}</li>
                            <li>{t('benefit2')}</li>
                            <li>{t('benefit3')}</li>
                            <li>{t('benefit4')}</li>
                            <li>{t('benefit5')}</li>
                            <li>{t('benefit6')}</li>
                        </ul>
                    </div>

                    {/* Right: Main Image */}
                    <div className="w-full lg:w-1/3">
                        <img
                            src={ayurvedaImage}
                            alt={t('mainImageAlt')}
                            className="w-full h-auto max-h-[500px] object-cover rounded-2xl shadow-2xl"
                            loading="lazy"
                        />
                    </div>
                </div>

                {/* Additional Images */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
                    <motion.div
                        whileHover={{ scale: 1.03 }}
                        className="group relative overflow-hidden rounded-2xl shadow-2xl"
                    >
                        <img
                            src={ayurvedaFaceImage}
                            alt={t('faceTreatmentAlt')}
                            className="w-full h-80 md:h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                            <p className="text-white text-lg font-semibold">{t('faceTreatmentCaption')}</p>
                        </div>
                    </motion.div>

                    <motion.div
                        whileHover={{ scale: 1.03 }}
                        className="group relative overflow-hidden rounded-2xl shadow-2xl"
                    >
                        <img
                            src={ayurvedaMassageImage}
                            alt={t('massageTreatmentAlt')}
                            className="w-full h-80 md:h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                            <p className="text-white text-lg font-semibold">{t('massageTreatmentCaption')}</p>
                        </div>
                    </motion.div>
                </div>

                {/* Treatment Description */}
                <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
                    <p className="text-gray-800 text-base md:text-lg lg:text-xl leading-relaxed font-inknut">
                        {t('treatmentDescription')}
                    </p>
                </div>

                {/* Tips Section */}
                <div className="mt-16">
                    <h2 className="text-center text-4xl md:text-5xl lg:text-6xl font-bold font-konkhmer mb-10 text-[#7FDBFF]">
                        {t('tipsTitle')}
                    </h2>

                    <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
                        <ul className="space-y-4 text-gray-800 text-base md:text-lg lg:text-xl font-inknut">
                            <li className="flex items-start">
                                <span className="text-[#0074D9] mr-3">•</span>
                                <span>{t('tip1')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-[#0074D9] mr-3">•</span>
                                <span>{t('tip2')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-[#0074D9] mr-3">•</span>
                                <span>{t('tip3')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-[#0074D9] mr-3">•</span>
                                <span>{t('tip4')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-[#0074D9] mr-3">•</span>
                                <span>{t('tip5')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-[#0074D9] mr-3">•</span>
                                <span>{t('tip6')}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AyurvedaPage;